import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import type { ProjectRow } from './types';

/**
 * The pieces the dashboard is built from.
 *
 * Every figure here carries its value as text. Colour marks sit beside the
 * words, never instead of them, so nothing on this screen depends on telling
 * two colours apart.
 */

// ── Panel ────────────────────────────────────────────────────────────────────

/** A card with a title row, an optional link out, and optional chart/table switch. */
export function Panel({
  title,
  subtitle,
  to,
  linkLabel = 'View all',
  actions,
  className,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  to?: string;
  linkLabel?: string;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn('flex flex-col rounded-xl border bg-card p-5 shadow-sm', className)}>
      <header className="mb-4 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {actions}
        {to && (
          <Link
            to={to}
            className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {linkLabel}
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </header>
      <div className="min-h-0 flex-1">{children}</div>
    </section>
  );
}

/** Chart or table — the same numbers either way, so a chart never gates a value. */
export function useChartTable() {
  const [mode, setMode] = useState<'chart' | 'table'>('chart');
  const toggle = (
    <div className="flex shrink-0 rounded-md border p-0.5 text-xs" role="group" aria-label="View">
      {(['chart', 'table'] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => setMode(option)}
          aria-pressed={mode === option}
          className={cn(
            'rounded px-2 py-0.5 capitalize transition-colors',
            mode === option ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground',
          )}
        >
          {option}
        </button>
      ))}
    </div>
  );
  return { mode, toggle };
}

// ── Stat tile ────────────────────────────────────────────────────────────────

/**
 * One number, what it is, and — where there is something honest to compare
 * with — which way it moved.
 */
export function StatTile({
  to,
  icon,
  label,
  value,
  title,
  hint,
  delta,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  /** The exact figure, for hover, when the tile shows a rounded one. */
  title?: string;
  hint?: React.ReactNode;
  delta?: { percent: number | null; against: string };
}) {
  return (
    <Link
      to={to}
      title={title}
      className="group flex flex-col rounded-xl border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </span>
        <span className="truncate text-xs font-medium text-muted-foreground">{label}</span>
        <ArrowRight className="ms-auto h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <div className="mt-3 truncate text-2xl font-semibold tracking-tight">{value}</div>
      <div className="mt-1 flex min-h-[1rem] flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
        {delta && <Delta percent={delta.percent} against={delta.against} />}
        {hint && <span className="truncate">{hint}</span>}
      </div>
    </Link>
  );
}

/**
 * "▲ 12% vs same days last month". Up is good for everything this dashboard
 * counts, so the direction and the tone agree; the arrow and the words carry
 * it, not the colour.
 */
function Delta({ percent, against }: { percent: number | null; against: string }) {
  // Nothing to compare with — say so plainly rather than print an infinity.
  if (percent === null) return <span>none this time last month</span>;

  const Icon = percent > 0 ? ArrowUpRight : percent < 0 ? ArrowDownRight : Minus;
  return (
    <span
      className={cn(
        'flex items-center gap-0.5 font-medium',
        percent > 0 && 'text-emerald-700',
        percent < 0 && 'text-red-700',
      )}
    >
      <Icon className="h-3 w-3" />
      {percent > 0 ? '+' : ''}
      {percent}%<span className="font-normal text-muted-foreground">&nbsp;{against}</span>
    </span>
  );
}

// ── Mini stat (inside the hero) ──────────────────────────────────────────────

export function MiniStat({
  to,
  label,
  value,
  title,
  hint,
}: {
  to: string;
  label: string;
  value: string;
  title?: string;
  hint?: string;
}) {
  return (
    <Link
      to={to}
      title={title}
      className="group rounded-lg border bg-background/60 px-3 py-2.5 transition-colors hover:bg-muted"
    >
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-lg font-semibold tracking-tight">{value}</div>
      {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
    </Link>
  );
}

// ── Bar list ─────────────────────────────────────────────────────────────────

/**
 * Horizontal bars with their labels and values written out — a chart and its
 * own table in one. Single series, so one colour for every bar; the length is
 * the value and the words say which is which.
 */
export function BarList({
  rows,
  empty,
}: {
  rows: { key: string; label: string; value: number; display: string; detail?: string; title?: string }[];
  empty: string;
}) {
  const max = Math.max(0, ...rows.map((row) => row.value));
  if (rows.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;

  return (
    <ul className="space-y-3">
      {rows.map((row) => {
        const width = max > 0 ? Math.max((row.value / max) * 100, row.value > 0 ? 2 : 0) : 0;
        return (
          <li key={row.key} title={row.title}>
            <div className="mb-1 flex items-baseline gap-2 text-xs">
              <span className="min-w-0 flex-1 truncate font-medium">{row.label}</span>
              {row.detail && <span className="shrink-0 text-muted-foreground">{row.detail}</span>}
              <span className="tabular shrink-0 font-semibold">{row.display}</span>
            </div>
            {/* The track is a lighter step of the same hue, so a short bar
                still reads as a share of something. */}
            <div className="h-2.5 w-full overflow-hidden rounded bg-[color-mix(in_srgb,var(--chart-1)_12%,transparent)]">
              <div
                className="h-full rounded-e bg-[var(--chart-1)] transition-[width] duration-500"
                style={{ width: `${width}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ── Split meter ──────────────────────────────────────────────────────────────

/**
 * A whole in two parts, as one bar with a surface gap between them. Two
 * segments is the whole reason this is a bar and not a pie: close values are
 * easy to compare along a line and hard to compare as angles.
 */
export function SplitMeter({
  parts,
}: {
  parts: { key: string; label: string; value: number; colour: string; to: string }[];
}) {
  const total = parts.reduce((sum, part) => sum + part.value, 0);

  return (
    <div>
      <div className="flex h-3 w-full gap-[2px] overflow-hidden rounded bg-muted">
        {total > 0 &&
          parts
            .filter((part) => part.value > 0)
            .map((part) => (
              <div
                key={part.key}
                title={`${part.label}: ${part.value}`}
                className="h-full first:rounded-s last:rounded-e"
                style={{ width: `${(part.value / total) * 100}%`, background: part.colour }}
              />
            ))}
      </div>
      <ul className="mt-3 space-y-1.5">
        {parts.map((part) => (
          <li key={part.key}>
            <Link to={part.to} className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: part.colour }} />
              <span className="flex-1">{part.label}</span>
              <span className="tabular font-semibold">{part.value}</span>
              <span className="tabular w-10 text-end text-xs text-muted-foreground">
                {total > 0 ? `${Math.round((part.value / total) * 100)}%` : '—'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Project list ─────────────────────────────────────────────────────────────

const HEALTH: Record<ProjectRow['health'], { label: string; colour: string }> = {
  GREEN: { label: 'On track', colour: 'var(--status-good)' },
  AMBER: { label: 'At risk', colour: 'var(--status-warning)' },
  RED: { label: 'Off track', colour: 'var(--status-critical)' },
};

export function ProjectList({
  projects,
  when,
  empty,
}: {
  projects: ProjectRow[];
  /** Which date to show: the finish for work under way, the start for work to come. */
  when: 'finish' | 'start';
  empty: string;
}) {
  if (projects.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>;
  }

  return (
    <ul className="divide-y">
      {projects.map((project) => {
        const date = when === 'finish' ? project.targetEndDate : project.startDate;
        const late =
          when === 'finish' && project.targetEndDate !== null && new Date(project.targetEndDate) < new Date();
        const health = HEALTH[project.health];
        return (
          <li key={project.id}>
            <Link
              to={`/delivery/projects/${project.id}`}
              className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-muted"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{project.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {project.company.name} · {project.number}
                </div>
              </div>
              {when === 'finish' && (
                // Status wears its colour beside its name, never alone.
                <span className="hidden shrink-0 items-center gap-1.5 text-xs text-muted-foreground sm:flex">
                  <span className="h-2 w-2 rounded-full" style={{ background: health.colour }} />
                  {health.label}
                </span>
              )}
              <span
                className={cn(
                  'w-24 shrink-0 text-end text-xs',
                  late ? 'font-medium text-red-700' : 'text-muted-foreground',
                )}
              >
                {date ? (
                  <>
                    <span className="block text-[10px] uppercase tracking-wide">
                      {when === 'finish' ? (late ? 'Was due' : 'Due') : 'Starts'}
                    </span>
                    {formatDate(date, 'd MMM yyyy')}
                  </>
                ) : (
                  'No date'
                )}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

// ── Section heading ──────────────────────────────────────────────────────────

export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h2>
  );
}
